// Material Take-Off helpers shared by the hub's screens.
// The server derives every quantity and line status when a take-off is saved; the functions here mirror
// those rules (app/services/material_takeoff_service.py) only so the editor can preview them while typing.

export const NAVY = '#0b318b';

export const TAKEOFF_STATUS = {
  draft:            { label: 'Draft',            bg: '#f1f5f9', color: '#334155' },
  pending_approval: { label: 'Pending Approval', bg: '#fef9c3', color: '#854d0e' },
  returned:         { label: 'Returned',         bg: '#ffedd5', color: '#9a3412' },
  rejected:         { label: 'Rejected',         bg: '#fee2e2', color: '#991b1b' },
  approved:         { label: 'Approved',         bg: '#dcfce7', color: '#166534' },
  cancelled:        { label: 'Cancelled',        bg: '#e5e7eb', color: '#4b5563' },
  superseded:       { label: 'Superseded',       bg: '#ede9fe', color: '#5b21b6' },
};

export const LINE_STATUS = {
  not_started:        { label: 'Not Started',        bg: '#f1f5f9', color: '#475569' },
  partial:            { label: 'Partial',            bg: '#fef3c7', color: '#92400e' },
  complete:           { label: 'Complete',           bg: '#dcfce7', color: '#166534' },
  requires_breakdown: { label: 'Requires Breakdown', bg: '#e0e7ff', color: '#3730a3' },
  new_material:       { label: 'New Material',       bg: '#fce7f3', color: '#9d174d' },
  clarification:      { label: 'Clarification',      bg: '#fee2e2', color: '#991b1b' },
};

export const LINE_TYPES = [
  { value: 'simple_material', label: 'Simple Material' },
  { value: 'lot', label: 'Lot (break into items)' },
];

// Systems Base44 offers when starting a take-off; any other name can be typed.
export const SYSTEM_SUGGESTIONS = [
  'Soil & Waste', 'Site Drainage Services', 'Water Supply', 'Cold Water Services', 'Hot Water Services',
  'Water Plant Filtration', 'Rainwater Drainage', 'Drainage', 'Fire Fighting', 'HVAC', 'Ductwork', 'Gas',
  'LV Power Distribution', 'Lighting', 'Small Power', 'Earthing & Lightning', 'ELV / Data', 'Equipment', 'Accessories', 'Other',
];

export const LIFECYCLE = [
  'Take-Off', 'Take-Off Approval', 'Material Request', 'Cost Control Review', 'Store Review', 'Management Approval',
  'Procurement Request', 'Quotation Comparison', 'Supplier Selection', 'Purchase Order', 'GRN', 'Store Receipt', 'Site Issue',
];

export const EDITABLE = ['draft', 'returned'];

const LOT_UOMS = ['lot', 'lots', 'ls', 'l.s', 'l/s', 'lump sum', 'sum', 'item'];
export const isLotUom = (u) => LOT_UOMS.includes(String(u || '').trim().toLowerCase());

export const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const fmt = (v, dp = 2) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: dp });
};

export const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const round4 = (n) => Math.round(n * 10000) / 10000;

// total in the measured unit, procurement in the purchase unit (rounded up)
export const materialQuantities = (m) => {
  const total = round4(num(m.required_quantity) * (1 + num(m.additional_percentage) / 100));
  const factor = num(m.conversion_factor) > 0 ? num(m.conversion_factor) : 1;
  const procurement = total > 0 ? Math.ceil(round4(total / factor)) : 0;
  return { total, procurement };
};

const normUom = (u) => String(u || '').toLowerCase().replace(/[^a-z]/g, '').replace(/s$/, '');
export const sameUom = (a, b) => !!(a && b) && normUom(a) === normUom(b);

export const engineeringQuantity = (line) => {
  const same = (line.materials || []).filter(m => sameUom(m.measured_uom, line.boq_uom));
  return same.length ? round4(same.reduce((s, m) => s + materialQuantities(m).total, 0)) : null;
};

export const lineStatus = (line) => {
  const mats = line.materials || [];
  if (line.needs_clarification) return 'clarification';
  if (!mats.length) return line.line_type === 'lot' ? 'requires_breakdown' : 'not_started';
  if (mats.some(m => m.is_new_material && !m.material_id)) return 'new_material';
  if (line.line_type === 'lot') return 'complete';
  const eng = engineeringQuantity(line);
  if (eng !== null && num(line.boq_quantity) && eng < num(line.boq_quantity)) return 'partial';
  return 'complete';
};

let seq = 0;
export const tmpId = () => `tmp-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export const blankMaterial = () => ({
  _key: tmpId(), material_id: null, material_code: '', description: '', specification: '', brand: '', is_new_material: false,
  required_quantity: '', measured_uom: '', conversion_factor: '1', purchase_uom: '', additional_percentage: '0', remarks: '',
});

export const blankLine = () => ({
  _key: tmpId(), boq_item_id: null, boq_code: '', boq_description: '', boq_quantity: '', boq_uom: '', total_transferred: 0,
  line_type: 'simple_material', needs_clarification: false, remarks: '', materials: [],
});

// server response → editable working copy
export const toWorking = (takeoff) => (takeoff.systems || []).map(s => ({
  _key: s.id, id: s.id, system_name: s.system_name, boq_id: s.boq_id, boq_name: s.boq_name,
  boq_lines: (s.boq_lines || []).map(l => ({
    _key: l.id, id: l.id, boq_item_id: l.boq_item_id, boq_code: l.boq_code || '', boq_description: l.boq_description,
    boq_quantity: l.boq_quantity ?? '', boq_uom: l.boq_uom || '', total_transferred: l.total_transferred, balance: l.balance,
    line_type: l.line_type, needs_clarification: l.line_status === 'clarification', remarks: l.remarks || '',
    materials: (l.materials || []).map(m => ({
      _key: m.id, material_id: m.material_id, material_code: m.material_code || '', description: m.description,
      specification: m.specification || '', brand: m.brand || '', is_new_material: m.is_new_material,
      required_quantity: m.required_quantity ?? '', measured_uom: m.measured_uom || '', conversion_factor: m.conversion_factor ?? '1',
      purchase_uom: m.purchase_uom || '', additional_percentage: m.additional_percentage ?? '0', remarks: m.remarks || '',
    })),
  })),
}));

const blankToNull = (v) => (v === '' || v === undefined ? null : v);

// editable working copy → the PUT payload (the server recomputes every derived figure)
export const toPayloadSystems = (systems) => systems.map(s => ({
  system_name: s.system_name,
  boq_id: s.boq_id || null,
  boq_lines: s.boq_lines.map(l => ({
    boq_item_id: l.boq_item_id || null,
    boq_code: blankToNull(l.boq_code),
    boq_description: l.boq_description,
    boq_quantity: l.boq_item_id ? null : blankToNull(l.boq_quantity),
    boq_uom: blankToNull(l.boq_uom),
    line_type: l.line_type,
    needs_clarification: !!l.needs_clarification,
    remarks: blankToNull(l.remarks),
    materials: l.materials.map(m => ({
      material_id: m.material_id || null,
      material_code: blankToNull(m.material_code),
      description: m.description,
      specification: blankToNull(m.specification),
      brand: blankToNull(m.brand),
      is_new_material: !m.material_id && !!m.is_new_material,
      required_quantity: num(m.required_quantity),
      measured_uom: blankToNull(m.measured_uom),
      conversion_factor: num(m.conversion_factor) > 0 ? num(m.conversion_factor) : 1,
      purchase_uom: blankToNull(m.purchase_uom),
      additional_percentage: num(m.additional_percentage),
      remarks: blankToNull(m.remarks),
    })),
  })),
}));

// the first problem that would stop the server accepting the save, or null
export const validateSystems = (systems) => {
  for (const s of systems) {
    if (!String(s.system_name || '').trim()) return 'Every system needs a name.';
    for (const l of s.boq_lines) {
      if (!String(l.boq_description || '').trim()) return `${s.system_name}: every BOQ line needs a description.`;
      for (const m of l.materials) {
        if (!String(m.description || '').trim()) return `${s.system_name} · ${l.boq_code || l.boq_description}: every material needs a description.`;
        if (m.required_quantity === '' || num(m.required_quantity) < 0) return `${s.system_name} · ${m.description}: enter the required quantity.`;
      }
    }
  }
  return null;
};

export const roleNames = (user) => (user?.roles || []).map(r => (typeof r === 'string' ? r : (r.role_name || r.name || '')).toLowerCase());
